"use strict";

/*==============================================================================*
*TRESTLE™ W011
*TRESTLE WELLNESS TRACKER
*ENGINE
*Version 1.0
*
*PILOT RESPONSIBILITIES
*----------------------
*• One-time invitation after first W002 profile completion
*• Request browser notification permission only after user action
*• Remember Tracker ON / OFF preference
*• Allow "Pause for today"
*• Run generic workday notification schedule
*• Prevent duplicate notifications for the same slot/day
*• Route wellness notifications to W005
*• Route yesterday's Insight notification to W007
*
*IMPORTANT
*---------
*• TRESTLE reminds. The user decides.
*• No medication instruction.
*• No medical diagnosis.
*• Phase 1 requires the TRESTLE page to remain open.
*==============================================================================*/


(function(){

"use strict";


/*==============================================================================*
*CONFIGURATION
*==============================================================================*/

const CONFIG={

    stateKey:
        "trestle_w011_wellness_tracker",

    profileKey:
        "trestleProfile",

    version:
        "1.0",

    schedulerInterval:
        60000,

    notificationGraceMinutes:
        30,

    messageDuration:
        5000

};


/*==============================================================================*
*RUNTIME
*==============================================================================*/

let root=null;

let schedulerTimer=null;

let messageTimer=null;

let eventsBound=false;


/*==============================================================================*
*DEFAULT STATE
*==============================================================================*/

function createDefaultState(){

    return{

        version:
            CONFIG.version,

        enabled:
            false,

        promptSeen:
            false,

        pausedDate:
            "",

        firedByDate:
            {},

        lastVariantBySlot:
            {}

    };

}


/*==============================================================================*
*READ STATE
*==============================================================================*/

function readState(){

    try{

        const raw=
            localStorage.getItem(
                CONFIG.stateKey
            );


        if(!raw){

            return createDefaultState();

        }


        const parsed=
            JSON.parse(raw);


        const base=
            createDefaultState();


        return Object.assign(
            base,
            parsed || {}
        );

    }

    catch(error){

        console.warn(
            "TRESTLE W011 state read error",
            error
        );


        return createDefaultState();

    }

}


/*==============================================================================*
*WRITE STATE
*==============================================================================*/

function writeState(state){

    try{

        state.version=
            CONFIG.version;


        localStorage.setItem(
            CONFIG.stateKey,
            JSON.stringify(state)
        );


        return true;

    }

    catch(error){

        console.warn(
            "TRESTLE W011 state save error",
            error
        );


        return false;

    }

}


/*==============================================================================*
*LOCAL DATE KEY
*==============================================================================*/

function getDateKey(date){

    const value=
        date || new Date();


    const year=
        value.getFullYear();


    const month=
        String(
            value.getMonth()+1
        ).padStart(
            2,
            "0"
        );


    const day=
        String(
            value.getDate()
        ).padStart(
            2,
            "0"
        );


    return(
        year+
        "-" +
        month+
        "-" +
        day
    );

}


/*==============================================================================*
*YESTERDAY DATE KEY
*==============================================================================*/

function getYesterdayKey(){

    const date=
        new Date();


    date.setDate(
        date.getDate()-1
    );


    return getDateKey(
        date
    );

}


/*==============================================================================*
*TIME TO MINUTES
*==============================================================================*/

function timeToMinutes(value){

    if(!value){

        return null;

    }


    const parts=
        String(value)
        .split(":");


    if(parts.length!==2){

        return null;

    }


    const hour=
        Number(parts[0]);


    const minute=
        Number(parts[1]);


    if(
        !Number.isFinite(hour) ||
        !Number.isFinite(minute)
    ){

        return null;

    }


    return(
        hour*60+
        minute
    );

}


/*==============================================================================*
*CURRENT MINUTES
*==============================================================================*/

function getCurrentMinutes(){

    const now=
        new Date();


    return(
        now.getHours()*60+
        now.getMinutes()
    );

}


/*==============================================================================*
*DATA
*==============================================================================*/

function getTrackerData(){

    if(
        typeof TRESTLE_W011_WELLNESS_TRACKER_DATA
        ==="undefined"
    ){

        return null;

    }


    return TRESTLE_W011_WELLNESS_TRACKER_DATA;

}


/*==============================================================================*
*NOTIFICATION SUPPORT
*==============================================================================*/

function notificationsSupported(){

    return(
        "Notification" in window
    );

}


/*==============================================================================*
*PROFILE COMPLETE
*==============================================================================*/

function isProfileComplete(){

    try{

        const raw=
            localStorage.getItem(
                CONFIG.profileKey
            );


        if(!raw){

            return false;

        }


        const profile=
            JSON.parse(raw);


        return Boolean(

            profile &&
            profile.promise &&
            profile.lifeStage &&
            profile.diabetesType

        );

    }

    catch(error){

        return false;

    }

}


/*==============================================================================*
*SHOW OPT-IN
*==============================================================================*/

function showOptInOnce(){

    if(!root){

        return;

    }


    const state=
        readState();


    if(
        state.promptSeen ||
        state.enabled
    ){

        return;

    }


    if(
        !isProfileComplete()
    ){

        return;

    }


    /*
    Mark as seen when first presented.
    This prevents the user being repeatedly interrupted.
    */

    state.promptSeen=true;

    writeState(state);


    const modal=
        root.querySelector(
            "#trW011OptInModal"
        );


    if(modal){

        modal.hidden=false;

    }

}


/*==============================================================================*
*CLOSE OPT-IN
*==============================================================================*/

function closeOptIn(){

    if(!root){

        return;

    }


    const modal=
        root.querySelector(
            "#trW011OptInModal"
        );


    if(modal){

        modal.hidden=true;

    }

}


/*==============================================================================*
*REQUEST PERMISSION
*==============================================================================*/

async function enableTracker(){

    if(
        !notificationsSupported()
    ){

        showMessage(
            "Desktop notifications are not supported by this browser."
        );

        return false;

    }


    let permission=
        Notification.permission;


    if(
        permission==="default"
    ){

        try{

            permission=
                await Notification.requestPermission();

        }

        catch(error){

            console.warn(
                "TRESTLE notification permission error",
                error
            );

            return false;

        }

    }


    if(
        permission!=="granted"
    ){

        const state=
            readState();


        state.enabled=false;

        writeState(state);


        updateUI();


        closeOptIn();


        showMessage(
            "Notifications were not enabled. You can allow them later from your browser settings."
        );


        return false;

    }


    const state=
        readState();


    state.enabled=true;

    state.pausedDate="";

    writeState(state);


    closeOptIn();

    updateUI();

    startScheduler();


    showMessage(
        "TRESTLE Wellness Tracker is on."
    );


    return true;

}


/*==============================================================================*
*DISABLE TRACKER
*==============================================================================*/

function disableTracker(){

    const state=
        readState();


    state.enabled=false;

    state.pausedDate="";


    writeState(state);


    stopScheduler();

    updateUI();


    showMessage(
        "TRESTLE Wellness Tracker is off."
    );

}


/*==============================================================================*
*PAUSE TODAY
*==============================================================================*/

function pauseToday(){

    const state=
        readState();


    state.pausedDate=
        getDateKey();


    writeState(state);


    updateUI();


    showMessage(
        "Wellness Tracker paused for today."
    );

}


/*==============================================================================*
*IS PAUSED TODAY
*==============================================================================*/

function isPausedToday(state){

    return(
        state.pausedDate===
        getDateKey()
    );

}


/*==============================================================================*
*NEW DAY CLEANUP
*==============================================================================*/

function normalizeDailyState(){

    const state=
        readState();


    /*
    A pause automatically expires on the next calendar day.
    */

    if(
        state.pausedDate &&
        state.pausedDate!==
        getDateKey()
    ){

        state.pausedDate="";

    }


    /*
    Keep only a small recent fired history.
    */

    const dates=
        Object.keys(
            state.firedByDate || {}
        )
        .sort()
        .reverse();


    dates
        .slice(7)
        .forEach(

            function(dateKey){

                delete state.firedByDate[
                    dateKey
                ];

            }

        );


    writeState(state);

}


/*==============================================================================*
*HAS FIRED
*==============================================================================*/

function hasFired(slotId){

    const state=
        readState();


    const today=
        getDateKey();


    const fired=
        state.firedByDate &&
        state.firedByDate[today];


    return Boolean(
        fired &&
        fired[slotId]
    );

}


/*==============================================================================*
*MARK FIRED
*==============================================================================*/

function markFired(slotId){

    const state=
        readState();


    const today=
        getDateKey();


    if(
        !state.firedByDate ||
        typeof state.firedByDate!=="object"
    ){

        state.firedByDate={};

    }


    if(
        !state.firedByDate[today]
    ){

        state.firedByDate[today]={};

    }


    state.firedByDate[today][slotId]=
        new Date().toISOString();


    writeState(state);

}


/*==============================================================================*
*YESTERDAY INSIGHT AVAILABLE
*==============================================================================*/

function yesterdayInsightAvailable(){

    const yesterday=
        getYesterdayKey();


    /*
    Preferred W007 engine interface.
    */

    try{

        if(
            window.TrestleW007Engine &&
            typeof window.TrestleW007Engine.getSavedInsight
            ==="function"
        ){

            return Boolean(

                window.TrestleW007Engine
                .getSavedInsight(
                    yesterday
                )

            );

        }

    }

    catch(error){

        console.warn(
            "W011 could not check W007 Insight",
            error
        );

    }


    /*
    If W007 cannot confirm an Insight,
    W011 remains silent rather than making a false claim.
    */

    return false;

}


/*==============================================================================*
*CONDITIONAL SLOT
*==============================================================================*/

function conditionPassed(slot){

    if(
        !slot ||
        slot.conditional!==true
    ){

        return true;

    }


    if(
        slot.type==="insight"
    ){

        return yesterdayInsightAvailable();

    }


    return false;

}


/*==============================================================================*
*CHOOSE MESSAGE
*==============================================================================*/

function chooseMessage(slot){

    if(
        !slot ||
        !Array.isArray(slot.messages) ||
        !slot.messages.length
    ){

        return"";

    }


    const state=
        readState();


    const previousIndex=
        state.lastVariantBySlot[
            slot.id
        ];


    let indexes=
        slot.messages
        .map(

            function(
                message,
                index
            ){

                return index;

            }

        );


    if(
        indexes.length>1 &&
        typeof previousIndex==="number"
    ){

        indexes=
            indexes.filter(

                function(index){

                    return index!==previousIndex;

                }

            );

    }


    const selectedIndex=
        indexes[
            Math.floor(
                Math.random()*
                indexes.length
            )
        ];


    state.lastVariantBySlot[
        slot.id
    ]=
        selectedIndex;


    writeState(state);


    return slot.messages[
        selectedIndex
    ];

}


/*==============================================================================*
*DESTINATION
*==============================================================================*/

function getDestination(slot){

    const data=
        getTrackerData();


    if(
        !data ||
        !slot ||
        !slot.destination
    ){

        return null;

    }


    return(
        data.destinations[
            slot.destination
        ] ||
        null
    );

}


/*==============================================================================*
*FOCUS DESTINATION
*==============================================================================*/

function focusDestination(destination){

    if(!destination){

        return;

    }


    try{

        window.focus();

    }

    catch(error){
        /* no action */
    }


    const target=
        document.getElementById(
            destination.targetId
        );


    if(target){

        target.scrollIntoView({

            behavior:"smooth",

            block:"start"

        });


        /*
        Make the destination briefly focusable for accessibility.
        */

        if(
            !target.hasAttribute(
                "tabindex"
            )
        ){

            target.setAttribute(
                "tabindex",
                "-1"
            );

        }


        try{

            target.focus({
                preventScroll:true
            });

        }

        catch(error){
            /* no action */
        }

    }

}


/*==============================================================================*
*SEND NOTIFICATION
*==============================================================================*/

function sendNotification(slot){

    if(
        !notificationsSupported() ||
        Notification.permission!=="granted"
    ){

        return false;

    }


    const message=
        chooseMessage(slot);


    if(!message){

        return false;

    }


    const destination=
        getDestination(slot);


    let notification;


    try{

        notification=
            new Notification(

                getTrackerData().title,

                {

                    body:
                        message,

                    icon:
                        "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Logo_browser.webp?v=1786511452",

                    tag:
                        "trestle-w011-" +
                        slot.id,

                    renotify:
                        false,

                    silent:
                        false

                }

            );

    }

    catch(error){

        console.warn(
            "TRESTLE notification error",
            error
        );


        return false;

    }


    notification.onclick=
        function(){

            focusDestination(
                destination
            );


            try{

                notification.close();

            }

            catch(error){
                /* no action */
            }

        };


    return true;

}


/*==============================================================================*
*CHECK SCHEDULE
*==============================================================================*/

function checkSchedule(){

    const data=
        getTrackerData();


    if(
        !data ||
        !Array.isArray(data.schedule)
    ){

        return;

    }


    normalizeDailyState();


    const state=
        readState();


    if(
        !state.enabled ||
        isPausedToday(state)
    ){

        return;

    }


    if(
        !notificationsSupported() ||
        Notification.permission!=="granted"
    ){

        return;

    }


    const currentMinutes=
        getCurrentMinutes();


    const workdayStart=
        timeToMinutes(
            data.workdayStart
        );


    const workdayEnd=
        timeToMinutes(
            data.workdayEnd
        );


    if(
        currentMinutes<
        workdayStart ||
        currentMinutes>
        workdayEnd
    ){

        return;

    }


    data.schedule.forEach(

        function(slot){

            if(
                !slot ||
                !slot.id ||
                !slot.time
            ){

                return;

            }


            if(
                hasFired(
                    slot.id
                )
            ){

                return;

            }


            const scheduledMinutes=
                timeToMinutes(
                    slot.time
                );


            if(
                scheduledMinutes===null
            ){

                return;

            }


            const minutesLate=
                currentMinutes-
                scheduledMinutes;


            /*
            Do not fire early.
            Do not resurrect reminders much later in the day.
            */

            if(
                minutesLate<0 ||
                minutesLate>
                CONFIG.notificationGraceMinutes
            ){

                return;

            }


            if(
                !conditionPassed(
                    slot
                )
            ){

                /*
                Mark conditional slot as handled for today
                once its time has arrived.

                This prevents repeated checking throughout
                the same notification window.
                */

                if(
                    slot.conditional===true
                ){

                    markFired(
                        slot.id
                    );

                }


                return;

            }


            if(
                sendNotification(
                    slot
                )
            ){

                markFired(
                    slot.id
                );

            }

        }

    );

}


/*==============================================================================*
*START SCHEDULER
*==============================================================================*/

function startScheduler(){

    stopScheduler();


    const state=
        readState();


    if(
        !state.enabled ||
        isPausedToday(state)
    ){

        return;

    }


    /*
    Run once immediately in case the page was opened
    during a notification window.
    */

    checkSchedule();


    schedulerTimer=
        window.setInterval(

            checkSchedule,

            CONFIG.schedulerInterval

        );

}


/*==============================================================================*
*STOP SCHEDULER
*==============================================================================*/

function stopScheduler(){

    if(schedulerTimer){

        window.clearInterval(
            schedulerTimer
        );


        schedulerTimer=null;

    }

}


/*==============================================================================*
*SHOW IN-WORKSPACE MESSAGE
*==============================================================================*/

function showMessage(message){

    if(!root){

        return;

    }


    const box=
        root.querySelector(
            "#trW011Message"
        );


    if(!box){

        return;

    }


    if(messageTimer){

        clearTimeout(
            messageTimer
        );

    }


    box.textContent=
        message;


    box.hidden=false;


    messageTimer=
        setTimeout(

            function(){

                box.hidden=true;

            },

            CONFIG.messageDuration

        );

}


/*==============================================================================*
*UPDATE UI
*==============================================================================*/

function updateUI(){

    if(!root){

        return;

    }


    const state=
        readState();


    const control=
        root.querySelector(
            ".tr-w011-control"
        );


    const status=
        root.querySelector(
            "#trW011Status"
        );


    const toggle=
        root.querySelector(
            "#trW011Toggle"
        );


    const pause=
        root.querySelector(
            "#trW011PauseToday"
        );


    const note=
        root.querySelector(
            "#trW011StatusNote"
        );


    const active=
        state.enabled===true;


    const paused=
        isPausedToday(
            state
        );


    if(control){

        control.classList.toggle(
            "is-active",
            active
        );

    }


    if(status){

        if(
            active &&
            paused
        ){

            status.textContent=
                "PAUSED";

        }

        else{

            status.textContent=
                active
                    ? "ON"
                    : "OFF";

        }

    }


    if(toggle){

        toggle.setAttribute(
            "aria-pressed",
            active
                ? "true"
                : "false"
        );


        toggle.textContent=
            active
                ? "Turn Off Wellness Tracker"
                : "Enable Wellness Tracker";

    }


    if(pause){

        pause.hidden=
            !active ||
            paused;

    }


    if(note){

        if(!notificationsSupported()){

            note.textContent=
                "Desktop notifications are not supported by this browser.";

        }

        else if(
            Notification.permission==="denied"
        ){

            note.textContent=
                "Notifications are blocked in your browser settings.";

        }

        else if(paused){

            note.textContent=
                "Reminders will resume automatically tomorrow.";

        }

        else if(active){

            note.textContent=
                "Gentle workday reminders are active.";

        }

        else{

            note.textContent=
                "Enable desktop notifications to begin.";

        }

    }

}


/*==============================================================================*
*TOGGLE
*==============================================================================*/

function handleToggle(){

    const state=
        readState();


    if(state.enabled){

        disableTracker();

    }

    else{

        enableTracker();

    }

}


/*==============================================================================*
*W002 PROFILE COMPLETION
*==============================================================================*/

function handleProfileCompletion(){

    if(
        !isProfileComplete()
    ){

        return;

    }


    /*
    Small delay allows W002 to finish its own save/close animation first.
    */

    window.setTimeout(

        showOptInOnce,

        450

    );

}


/*==============================================================================*
*BIND EVENTS
*==============================================================================*/

function bindEvents(){

    if(
        !root ||
        eventsBound
    ){

        return;

    }


    eventsBound=true;


    const toggle=
        root.querySelector(
            "#trW011Toggle"
        );


    const enable=
        root.querySelector(
            "#trW011Enable"
        );


    const notNow=
        root.querySelector(
            "#trW011NotNow"
        );


    const close=
        root.querySelector(
            "#trW011ModalClose"
        );


    const pause=
        root.querySelector(
            "#trW011PauseToday"
        );


    if(toggle){

        toggle.addEventListener(
            "click",
            handleToggle
        );

    }


    if(enable){

        enable.addEventListener(

            "click",

            function(){

                enableTracker();

            }

        );

    }


    if(notNow){

        notNow.addEventListener(
            "click",
            closeOptIn
        );

    }


    if(close){

        close.addEventListener(
            "click",
            closeOptIn
        );

    }


    if(pause){

        pause.addEventListener(
            "click",
            pauseToday
        );

    }


    root.addEventListener(

        "click",

        function(event){

            if(
                event.target.hasAttribute(
                    "data-w011-dismiss"
                )
            ){

                closeOptIn();

            }

        }

    );


    /*
    Current W002 compatibility.
    Listen to both event forms already used by the workspace.
    */

    window.addEventListener(
        "trestle-profile-updated",
        handleProfileCompletion
    );


    document.addEventListener(
        "trestle:profile-updated",
        handleProfileCompletion
    );


    /*
    Browser permission may be changed outside the page.
    Refresh UI when returning to TRESTLE.
    */

    document.addEventListener(

        "visibilitychange",

        function(){

            if(
                document.visibilityState==="visible"
            ){

                updateUI();

                if(
                    readState().enabled
                ){

                    startScheduler();

                }

            }

        }

    );

}


/*==============================================================================*
*INITIALIZE
*==============================================================================*/

function init(){

    root=
        document.getElementById(
            "trW011"
        );


    if(!root){

        return;

    }


    normalizeDailyState();

    bindEvents();

    updateUI();


    const state=
        readState();


    /*
    If the user already completed W002 but W011 has never
    presented its invitation, show it once on this visit.
    */

    if(
        isProfileComplete() &&
        !state.promptSeen &&
        !state.enabled
    ){

        window.setTimeout(
            showOptInOnce,
            900
        );

    }


    if(
        state.enabled &&
        notificationsSupported() &&
        Notification.permission==="granted"
    ){

        startScheduler();

    }


    console.log(
        "TRESTLE W011 Wellness Tracker Ready"
    );

}


/*==============================================================================*
*PUBLIC API
*==============================================================================*/

window.TrestleW011={

    enable:function(){

        return enableTracker();

    },


    disable:function(){

        disableTracker();

    },


    pauseToday:function(){

        pauseToday();

    },


    checkNow:function(){

        checkSchedule();

    },


    isEnabled:function(){

        return Boolean(
            readState().enabled
        );

    },


    getState:function(){

        return JSON.parse(
            JSON.stringify(
                readState()
            )
        );

    }

};


/*==============================================================================*
*BOOTSTRAP
*==============================================================================*/

if(
    document.readyState==="loading"
){

    document.addEventListener(
        "DOMContentLoaded",
        init
    );

}

else{

    init();

}


/*==============================================================================*
*CLOSE W011
*==============================================================================*/

})();